import React, { useEffect, useState, useRef } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import BlockEditor from '../components/BlockEditor';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './NewsManagement.css';

// TODO: Replace REACT_APP_MAPBOX_ACCESS_TOKEN with your Mapbox public token (pk.xxx)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
} else {
  console.warn('Mapbox token not set. Set REACT_APP_MAPBOX_ACCESS_TOKEN environment variable.');
}

export default function PlacesManagement() {
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    address: '',
    phone: '',
    email: '',
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [blocks, setBlocks] = useState([{ 
    type: 'text', 
    text: '', 
    align: 'left',
    bold: false,
    italic: false,
    underline: false
  }]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    loadPlaces();
    loadCategories();
  }, []);

  // Initialize Mapbox map (only when modal is open)
  useEffect(() => {
    if (!showModal) {
      // Cleanup when modal closes
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapLoaded(false);
      return;
    }

    if (!mapDivRef.current) return;
    if (!MAPBOX_TOKEN) {
      console.warn('Mapbox token not set');
      setMapLoaded(false);
      return;
    }

    // Cleanup existing map if any
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    const lat = formData.latitude ? parseFloat(formData.latitude) : 52.071809; // Bielawy center
    const lng = formData.longitude ? parseFloat(formData.longitude) : 19.661209;

    // Initialize map
    const map = new mapboxgl.Map({
      container: mapDivRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: 13,
    });

    mapRef.current = map;
    setMapLoaded(false);

    // Wait for map to load
    map.on('load', () => {
      setMapLoaded(true);

      // Create marker after map loads
      const marker = new mapboxgl.Marker({
        draggable: true,
      })
        .setLngLat([lng, lat])
        .addTo(map);

      markerRef.current = marker;

      // Update coordinates on marker drag
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        setFormData(prev => ({
          ...prev,
          latitude: lngLat.lat.toString(),
          longitude: lngLat.lng.toString(),
        }));
      });

      // Update coordinates on map click
      map.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        marker.setLngLat([lng, lat]);
        setFormData(prev => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString(),
        }));
      });
    });

    // Cleanup
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]); // Only re-initialize when modal opens/closes, not when coordinates change

  // Update marker position when formData coordinates change (for editing existing place)
  useEffect(() => {
    if (!showModal || !mapLoaded || !markerRef.current || !mapRef.current) return;

    const currentLat = formData.latitude ? parseFloat(formData.latitude) : null;
    const currentLng = formData.longitude ? parseFloat(formData.longitude) : null;
    
    if (currentLat && currentLng && !isNaN(currentLat) && !isNaN(currentLng)) {
      const markerLngLat = markerRef.current.getLngLat();
      const markerLat = markerLngLat.lat;
      const markerLng = markerLngLat.lng;
      
      // Only update if coordinates actually changed (avoid infinite loop)
      if (Math.abs(markerLat - currentLat) > 0.0001 || Math.abs(markerLng - currentLng) > 0.0001) {
        markerRef.current.setLngLat([currentLng, currentLat]);
        mapRef.current.flyTo({
          center: [currentLng, currentLat],
          zoom: 14,
          duration: 1000,
        });
      }
    }
  }, [showModal, mapLoaded, formData.latitude, formData.longitude]);

  // Convert blocks to HTML for backward compatibility (description field)
  function blocksToHtml(blocks) {
    if (!blocks || blocks.length === 0) return '';
    
    return blocks.map(block => {
      if (block.type === 'text') {
        let processed = block.text || '';
        
        if (block.bold) {
          processed = `<strong>${processed}</strong>`;
        }
        if (block.italic) {
          processed = `<em>${processed}</em>`;
        }
        if (block.underline) {
          processed = `<u>${processed}</u>`;
        }
        
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        return `<p ${alignStyle}>${processed}</p>`;
      } else if (block.type === 'divider') {
        return '<hr/>';
      } else if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      }
      return '';
    }).join('');
  }

  // Convert HTML/description to blocks (for backward compatibility)
  function htmlToBlocks(html) {
    if (!html) return [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
    
    const blocks = [];
    const parts = html.split(/(<p[^>]*>.*?<\/p>|<hr\s*\/?>)/gi);
    
    for (const part of parts) {
      if (!part || !part.trim()) continue;
      
      const trimmedPart = part.trim();
      
      if (/<hr\s*\/?>/i.test(trimmedPart)) {
        blocks.push({ type: 'divider' });
        continue;
      }
      
      const pMatch = trimmedPart.match(/<p[^>]*>(.*?)<\/p>/i);
      if (pMatch) {
        const pContent = pMatch[1];
        const pTag = pMatch[0];
        
        let align = 'left';
        const alignMatch = pTag.match(/text-align:\s*(left|center|right)/i);
        if (alignMatch) {
          align = alignMatch[1].toLowerCase();
        }
        
        const linkMatch = pContent.match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i);
        if (linkMatch) {
          blocks.push({
            type: 'link',
            text: linkMatch[2].trim(),
            url: linkMatch[1] || '',
            align: align
          });
          continue;
        }
        
        let text = pContent;
        let bold = /<strong>|<b>/i.test(text);
        let italic = /<em>|<i>/i.test(text);
        let underline = /<u>/i.test(text);
        
        text = text.replace(/<\/?strong>|<\/?b>/gi, '');
        text = text.replace(/<\/?em>|<\/?i>/gi, '');
        text = text.replace(/<\/?u>/gi, '');
        text = text.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: align,
            bold: bold,
            italic: italic,
            underline: underline
          });
        }
      } else if (!/<[^>]+>/.test(trimmedPart)) {
        const text = trimmedPart.replace(/&nbsp;/g, ' ').trim();
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: 'left',
            bold: false,
            italic: false,
            underline: false
          });
        }
      }
    }
    
    return blocks.length > 0 ? blocks : [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
  }

  const loadCategories = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('interactive_map_categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPlaces = async () => {
    try {
      // Load places first (simple query)
      const { data: placesData, error: placesError } = await supabaseAdmin
        .from('interactive_map_places')
        .select('*')
        .order('name', { ascending: true });

      if (placesError) throw placesError;

      // Load images separately
      const { data: imagesData, error: imagesError } = await supabaseAdmin
        .from('interactive_map_place_images')
        .select('*')
        .order('display_order', { ascending: true });

      if (imagesError) {
        console.warn('Error loading images:', imagesError);
      }

      // Load categories separately
      const { data: categoriesData, error: categoriesError } = await supabaseAdmin
        .from('interactive_map_place_categories')
        .select(`
          place_id,
          category_id,
          interactive_map_categories (*)
        `);

      if (categoriesError) {
        console.warn('Error loading place categories:', categoriesError);
      }

      // Combine data
      const placesWithRelations = (placesData || []).map(place => {
        const placeImages = (imagesData || []).filter(img => img.place_id === place.id);
        const placeCategories = (categoriesData || [])
          .filter(pc => pc && pc.place_id === place.id)
          .map(pc => ({
            place_id: pc.place_id,
            category_id: pc.category_id,
            interactive_map_categories: pc.interactive_map_categories || null,
          }));
        
        return {
          ...place,
          interactive_map_place_images: placeImages,
          interactive_map_place_categories: placeCategories,
        };
      });

      setPlaces(placesWithRelations);
    } catch (error) {
      toast.error('Błąd podczas ładowania miejsc');
      console.error('Error loading places:', error);
      setPlaces([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        invalidFiles.push(file.name);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Nieprawidłowe pliki: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      setGalleryFiles([...galleryFiles, ...validFiles]);

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setGalleryPreviews(prev => [...prev, { file, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const uploadGalleryImage = async (file, placeId) => {
    try {
      if (!placeId) {
        throw new Error('Place ID is required for image upload');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `places/${placeId}/${fileName}`;

      console.log('Uploading image to bucket "interactive-map", path:', filePath);

      const { data: uploadData, error: uploadError } = await supabaseAdmin.storage
        .from('interactive-map')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        console.error('Storage upload error details:', {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError.error,
          fullError: uploadError,
        });
        
        // Provide more helpful error message
        if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('does not exist')) {
          throw new Error(`Bucket "interactive-map" nie istnieje. Utwórz go w Supabase Dashboard > Storage > Buckets.`);
        }
        
        throw uploadError;
      }

      console.log('Upload successful, data:', uploadData);

      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('interactive-map')
        .getPublicUrl(filePath);

      console.log('Public URL:', publicUrl);

      return { url: publicUrl, path: filePath };
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      throw error;
    }
  };

  const deleteGalleryImage = async (imageId, imagePath) => {
    try {
      const { error: dbError } = await supabaseAdmin
        .from('interactive_map_place_images')
        .delete()
        .eq('id', imageId);

      if (dbError) throw dbError;

      // Extract path from URL or use image_path
      let filePath = imagePath;
      if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
        const urlParts = imagePath.split('/storage/v1/object/public/interactive-map/');
        if (urlParts.length > 1) {
          filePath = urlParts[1];
        } else {
          // Try old bucket name for backward compatibility
          const urlPartsOld = imagePath.split('/storage/v1/object/public/interactive_map_places/');
          if (urlPartsOld.length > 1) {
            filePath = urlPartsOld[1];
          } else {
            const pathParts = imagePath.split('/');
            filePath = pathParts[pathParts.length - 1];
          }
        }
      }

      const { error: storageError } = await supabaseAdmin.storage
        .from('interactive-map')
        .remove([filePath]);

      if (storageError) {
        console.warn('Error deleting from storage:', storageError);
      }

      toast.success('Zdjęcie usunięte');
      loadPlaces();
    } catch (error) {
      toast.error('Błąd podczas usuwania zdjęcia');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const htmlFromBlocks = blocksToHtml(blocks);
      
      if (!formData.name.trim() || !htmlFromBlocks.trim()) {
        toast.error('Wypełnij wszystkie wymagane pola');
        return;
      }

      if (!formData.latitude || !formData.longitude) {
        toast.error('Wprowadź współrzędne geograficzne');
        return;
      }

      const latitude = parseFloat(formData.latitude);
      const longitude = parseFloat(formData.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        toast.error('Współrzędne geograficzne muszą być liczbami');
        return;
      }

      let placeId;

      if (editingPlace) {
        const { data, error } = await supabaseAdmin
          .from('interactive_map_places')
          .update({
            name: formData.name.trim(),
            description: htmlFromBlocks,
            latitude: latitude,
            longitude: longitude,
            address: formData.address.trim() || null,
            phone: formData.phone.trim() || null,
            email: formData.email.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingPlace.id)
          .select()
          .single();

        if (error) throw error;
        placeId = data.id;
        toast.success('Miejsce zaktualizowane');
      } else {
        const { data, error } = await supabaseAdmin
          .from('interactive_map_places')
          .insert({
            name: formData.name.trim(),
            description: htmlFromBlocks,
            latitude: latitude,
            longitude: longitude,
            address: formData.address.trim() || null,
            phone: formData.phone.trim() || null,
            email: formData.email.trim() || null,
          })
          .select()
          .single();

        if (error) throw error;
        placeId = data.id;
        toast.success('Miejsce dodane');
      }

      // Update categories
      if (placeId) {
        // Delete existing category relationships
        await supabaseAdmin
          .from('interactive_map_place_categories')
          .delete()
          .eq('place_id', placeId);

        // Insert new category relationships
        if (selectedCategories.length > 0) {
          const categoryInserts = selectedCategories.map(catId => ({
            place_id: placeId,
            category_id: catId,
          }));

          const { error: categoryError } = await supabaseAdmin
            .from('interactive_map_place_categories')
            .insert(categoryInserts);

          if (categoryError) {
            console.error('Error saving categories:', categoryError);
            toast.error('Miejsce zapisane, ale wystąpił błąd podczas zapisywania kategorii');
          }
        }
      }

      // Upload gallery images (only after place is saved and we have placeId)
      if (galleryFiles.length > 0 && placeId) {
        setUploadingGallery(true);
        try {
          const galleryUploads = [];
          let uploadSuccessCount = 0;
          let uploadErrorCount = 0;

          for (let i = 0; i < galleryFiles.length; i++) {
            try {
              const { url, path } = await uploadGalleryImage(galleryFiles[i], placeId);
              galleryUploads.push({
                place_id: placeId,
                image_url: url,
                image_path: path,
                display_order: i,
              });
              uploadSuccessCount++;
            } catch (uploadError) {
              console.error(`Error uploading image ${i + 1}:`, uploadError);
              console.error('Upload error details:', {
                message: uploadError?.message,
                error: uploadError,
              });
              uploadErrorCount++;
            }
          }

          // Save successfully uploaded images to DB
          if (galleryUploads.length > 0) {
            const { error: galleryError } = await supabaseAdmin
              .from('interactive_map_place_images')
              .insert(galleryUploads);

            if (galleryError) {
              console.error('Error saving gallery images to DB:', galleryError);
              toast.error('Miejsce zapisane, ale wystąpił błąd podczas zapisywania galerii w bazie danych');
            } else if (uploadErrorCount > 0) {
              toast.error(`Miejsce zapisane. Przesłano ${uploadSuccessCount} z ${galleryFiles.length} zdjęć.`);
            }
          } else if (uploadErrorCount > 0) {
            const firstError = galleryFiles.length > 0 ? 'Sprawdź konsolę przeglądarki (F12) dla szczegółów.' : '';
            toast.error(`Miejsce zapisane, ale nie udało się przesłać żadnego zdjęcia. ${firstError}`);
            console.error('Wszystkie uploady zakończyły się niepowodzeniem. Sprawdź czy bucket "interactive-map" istnieje w Supabase Storage.');
          }
        } catch (galleryUploadError) {
          console.error('Error in gallery upload process:', galleryUploadError);
          toast.error('Miejsce zapisane, ale wystąpił błąd podczas przesyłania galerii');
        } finally {
          setUploadingGallery(false);
        }
      }

      setShowModal(false);
      setEditingPlace(null);
      setFormData({
        name: '',
        description: '',
        latitude: '',
        longitude: '',
        address: '',
        phone: '',
        email: '',
      });
      setSelectedCategories([]);
      setBlocks([{ 
        type: 'text', 
        text: '', 
        align: 'left',
        bold: false,
        italic: false,
        underline: false
      }]);
      setGalleryFiles([]);
      setGalleryPreviews([]);
      setExistingGalleryImages([]);
      
      // Reload places to get updated data
      await loadPlaces();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas zapisywania');
      console.error('Error saving place:', error);
    }
  };

  const handleEdit = async (item) => {
    setEditingPlace(item);
    setFormData({
      name: item.name,
      description: item.description,
      latitude: item.latitude.toString(),
      longitude: item.longitude.toString(),
      address: item.address || '',
      phone: item.phone || '',
      email: item.email || '',
    });
    
    if (item.blocks && Array.isArray(item.blocks) && item.blocks.length > 0) {
      const normalizedBlocks = item.blocks.map(block => {
        if (block.type === 'text') {
          return {
            ...block,
            bold: block.bold ?? false,
            italic: block.italic ?? false,
            underline: block.underline ?? false,
            align: block.align || 'left'
          };
        } else if (block.type === 'link') {
          return {
            ...block,
            text: block.text || '',
            url: block.url || '',
            align: block.align || 'left'
          };
        }
        return block;
      });
      setBlocks(normalizedBlocks);
    } else {
      const convertedBlocks = htmlToBlocks(item.description || '');
      setBlocks(convertedBlocks);
    }

    // Load categories directly from database for this place
    try {
      const { data: placeCategoriesData, error: categoriesError } = await supabaseAdmin
        .from('interactive_map_place_categories')
        .select('category_id')
        .eq('place_id', item.id);

      if (categoriesError) {
        console.error('Error loading place categories:', categoriesError);
        setSelectedCategories([]);
      } else {
        // Extract category_ids as array
        const categoryIds = (placeCategoriesData || [])
          .map(pc => pc.category_id)
          .filter(id => id != null);
        setSelectedCategories(categoryIds);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setSelectedCategories([]);
    }

    // Sort images by display_order
    const sortedImages = [...(item.interactive_map_place_images || [])].sort(
      (a, b) => (a.display_order || 0) - (b.display_order || 0)
    );
    setExistingGalleryImages(sortedImages);
    
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to miejsce?')) return;

    try {
      // 1. Pobierz wszystkie obrazy powiązane z tym miejscem
      const { data: placeImages, error: imagesError } = await supabaseAdmin
        .from('interactive_map_place_images')
        .select('id, image_path, image_url')
        .eq('place_id', id);

      if (imagesError) {
        console.warn('Error loading place images:', imagesError);
      }

      // 2. Usuń wszystkie obrazy z Storage
      if (placeImages && placeImages.length > 0) {
        const filePathsToDelete = placeImages
          .map(img => {
            let filePath = img.image_path || img.image_url;
            // Extract path from URL if needed
            if (filePath && (filePath.startsWith('http://') || filePath.startsWith('https://'))) {
              const urlParts = filePath.split('/storage/v1/object/public/interactive-map/');
              if (urlParts.length > 1) {
                filePath = urlParts[1];
              } else {
                // Try old bucket name for backward compatibility
                const urlPartsOld = filePath.split('/storage/v1/object/public/interactive_map_places/');
                if (urlPartsOld.length > 1) {
                  filePath = urlPartsOld[1];
                } else {
                  const pathParts = filePath.split('/');
                  filePath = pathParts[pathParts.length - 1];
                }
              }
            }
            return filePath;
          })
          .filter(path => path);

        if (filePathsToDelete.length > 0) {
          const { error: storageError } = await supabaseAdmin.storage
            .from('interactive-map')
            .remove(filePathsToDelete);

          if (storageError) {
            console.warn('Error deleting images from storage:', storageError);
            // Continue with deletion even if storage deletion fails
          } else {
            console.log(`✓ Deleted ${filePathsToDelete.length} image(s) from storage`);
          }
        }
      }

      // 3. Usuń rekordy z tabeli junction dla kategorii
      const { error: categoriesError } = await supabaseAdmin
        .from('interactive_map_place_categories')
        .delete()
        .eq('place_id', id);

      if (categoriesError) {
        console.warn('Error deleting place categories:', categoriesError);
        // Continue with deletion even if this fails
      }

      // 4. Usuń rekordy z tabeli obrazów
      const { error: imagesDeleteError } = await supabaseAdmin
        .from('interactive_map_place_images')
        .delete()
        .eq('place_id', id);

      if (imagesDeleteError) {
        console.warn('Error deleting place images from DB:', imagesDeleteError);
        // Continue with deletion even if this fails
      }

      // 5. Usuń samo miejsce
      const { error } = await supabaseAdmin
        .from('interactive_map_places')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Miejsce i powiązane zdjęcia zostały usunięte');
      loadPlaces();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania');
      console.error('Error deleting place:', error);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      // Simple includes check - UUIDs should match as-is
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>Zarządzanie miejscami - Mapa interaktywna</h2>
        <button className="btn btn-primary" onClick={() => {
          setEditingPlace(null);
          setFormData({
            name: '',
            description: '',
            latitude: '',
            longitude: '',
            address: '',
            phone: '',
            email: '',
          });
          setSelectedCategories([]);
          setBlocks([{ 
            type: 'text', 
            text: '', 
            align: 'left',
            bold: false,
            italic: false,
            underline: false
          }]);
      setGalleryFiles([]);
      setGalleryPreviews([]);
      setExistingGalleryImages([]);
      setShowModal(true);
        }}>
          Dodaj miejsce
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Lokalizacja</th>
              <th>Kategorie</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {places.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">Brak miejsc</td>
              </tr>
            ) : (
              places.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.address || `${item.latitude}, ${item.longitude}`}</td>
                  <td>
                    {item.interactive_map_place_categories && Array.isArray(item.interactive_map_place_categories) && item.interactive_map_place_categories.length > 0 ? (
                      item.interactive_map_place_categories
                        .filter(pc => pc && pc.interactive_map_categories)
                        .map((pc, idx) => (
                          <span key={pc.category_id || idx} className="badge badge-info" style={{ marginRight: '4px' }}>
                            {pc.interactive_map_categories?.name || 'N/A'}
                          </span>
                        ))
                    ) : (
                      <span style={{ color: '#999' }}>Brak kategorii</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(item)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          if (!uploadingGallery) {
            const wasEditing = editingPlace !== null;
            setShowModal(false);
            setEditingPlace(null);
            setFormData({
              name: '',
              description: '',
              latitude: '',
              longitude: '',
              address: '',
              phone: '',
              email: '',
            });
            // Only reset categories if not editing
            if (!wasEditing) {
              setSelectedCategories([]);
            }
            setBlocks([{ 
              type: 'text', 
              text: '', 
              align: 'left',
              bold: false,
              italic: false,
              underline: false
            }]);
            setGalleryFiles([]);
            setGalleryPreviews([]);
            setExistingGalleryImages([]);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingPlace ? 'Edytuj miejsce' : 'Dodaj miejsce'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  const wasEditing = editingPlace !== null;
                  setEditingPlace(null);
                  setFormData({
                    name: '',
                    description: '',
                    latitude: '',
                    longitude: '',
                    address: '',
                    phone: '',
                    email: '',
                  });
                  // Only reset categories if not editing
                  if (!wasEditing) {
                    setSelectedCategories([]);
                  }
                  setBlocks([{ 
                    type: 'text', 
                    text: '', 
                    align: 'left',
                    bold: false,
                    italic: false,
                    underline: false
                  }]);
                  setGalleryFiles([]);
                  setGalleryPreviews([]);
                  setExistingGalleryImages([]);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Nazwa *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Opis *</label>
                <BlockEditor blocks={blocks} onBlocksChange={setBlocks} />
              </div>
              <div className="form-group">
                <label className="form-label">Lokalizacja *</label>
                <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
                  Kliknij na mapie, aby ustawić lokalizację. Możesz przeciągać pinezkę.
                </div>
                {!MAPBOX_TOKEN ? (
                  <div style={{
                    width: '100%',
                    height: '400px',
                    marginBottom: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fee',
                    color: '#c00',
                  }}>
                    Brak tokenu Mapbox – ustaw REACT_APP_MAPBOX_ACCESS_TOKEN
                  </div>
                ) : (
                  <div
                    ref={mapDivRef}
                    style={{
                      width: '100%',
                      height: '400px',
                      marginBottom: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                )}
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                  <div>Współrzędne:</div>
                  <div style={{ marginTop: '4px' }}>
                    <strong>Szerokość:</strong> {formData.latitude || 'Nie ustawiono'}
                  </div>
                  <div>
                    <strong>Długość:</strong> {formData.longitude || 'Nie ustawiono'}
                  </div>
                </div>
                <input
                  type="hidden"
                  value={formData.latitude}
                  required
                />
                <input
                  type="hidden"
                  value={formData.longitude}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Adres</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Telefon</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Kategorie</label>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                  {categories.length === 0 ? (
                    <div style={{ color: '#999', fontSize: '14px' }}>Brak kategorii. Dodaj kategorie w sekcji "Kategorie - Mapa interaktywna".</div>
                  ) : (
                    categories.map((category) => (
                      <label key={category.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => toggleCategory(category.id)}
                          style={{ marginRight: '8px' }}
                        />
                        {category.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Zdjęcia miejsca</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGallerySelect}
                  className="form-input"
                />
                {uploadingGallery && (
                  <div className="upload-loader">Przesyłanie zdjęć...</div>
                )}
                {(galleryPreviews.length > 0 || existingGalleryImages.length > 0) && (
                  <div className="gallery-preview-container">
                    {galleryPreviews.map((preview, index) => (
                      <div key={`new-${index}`} className="gallery-item">
                        <img src={preview.preview} alt={`Preview ${index + 1}`} className="gallery-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeGalleryFile(index)}
                        >
                          Usuń
                        </button>
                      </div>
                    ))}
                    {existingGalleryImages.map((img) => (
                      <div key={img.id} className="gallery-item">
                        <img src={img.image_url} alt="Gallery" className="gallery-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => deleteGalleryImage(img.id, img.image_path || img.image_url)}
                        >
                          Usuń
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploadingGallery}
                >
                  {uploadingGallery ? 'Zapisywanie...' : (editingPlace ? 'Zaktualizuj' : 'Zapisz')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const wasEditing = editingPlace !== null;
                    setShowModal(false);
                    setEditingPlace(null);
                    setFormData({
                      name: '',
                      description: '',
                      latitude: '',
                      longitude: '',
                      address: '',
                      phone: '',
                      email: '',
                    });
                    // Only reset categories if not editing
                    if (!wasEditing) {
                      setSelectedCategories([]);
                    }
                    setBlocks([{ 
                      type: 'text', 
                      text: '', 
                      align: 'left',
                      bold: false,
                      italic: false,
                      underline: false
                    }]);
                    setGalleryFiles([]);
                    setGalleryPreviews([]);
                    setExistingGalleryImages([]);
                  }}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
